/**
 * O Santuário do Pentagrama no motor (techspec-pentagrama §09): onde ficam as pontas (camada `regioes` do mapa),
 * qual ponta ou o centro está ao alcance do jogador, e o desenho da chama de cada ponta, da orbe acesa e do ritual.
 * Só desenho e geometria: o servidor confere a posição de novo antes de qualquer forja ou engaste.
 */
export const MAPA_SANTUARIO = 'santuario-pentagrama';
export const CORES_PENTA = ['fogo', 'gelo', 'terra', 'ar', 'vazio'] as const;
export type CorPenta = (typeof CORES_PENTA)[number];
export const COR_HEX: Record<CorPenta, string> = {
  fogo: '#eb4628', gelo: '#5aaaff', terra: '#5ac85a', ar: '#ebebf5', vazio: '#aa5aeb'
};
/** Alcance da sala (RoomLoop.PentaAlcance): Chebyshev até o centro da região. */
export const ALCANCE_PENTA = 2;
export const TODAS_PENTA = 0b11111;

export type LugarPenta = { tipo: 'ponta'; cor: number } | { tipo: 'centro' } | null;

type Regiao = { nome: string; x: number; y: number; w: number; h: number };

export function centroDe(r: Regiao) {
  return { x: r.x + Math.floor(r.w / 2), y: r.y + Math.floor(r.h / 2) };
}

export function pontasDoMapa(regioes: readonly Regiao[]) {
  return CORES_PENTA.map(id => {
    const r = regioes.find(x => x.nome === `ponta-${id}`);
    return r ? centroDe(r) : null;
  });
}

export function centroDoMapa(regioes: readonly Regiao[]) {
  const r = regioes.find(x => x.nome === 'centro');
  return r ? centroDe(r) : null;
}

export function lugarPerto(regioes: readonly Regiao[], x: number, y: number): LugarPenta {
  const pontas = pontasDoMapa(regioes);
  for (let i = 0; i < pontas.length; i++) {
    const p = pontas[i];
    if (p && Math.max(Math.abs(p.x - x), Math.abs(p.y - y)) <= ALCANCE_PENTA) return { tipo: 'ponta', cor: i };
  }
  const c = centroDoMapa(regioes);
  if (c && Math.max(Math.abs(c.x - x), Math.abs(c.y - y)) <= ALCANCE_PENTA) return { tipo: 'centro' };
  return null;
}

export const chaveDoLugar = (l: LugarPenta) => (l ? (l.tipo === 'ponta' ? `p${l.cor}` : 'c') : '');

export const contarOrbes = (mascara: number) => {
  let n = 0;
  for (let i = 0; i < 5; i++) if (mascara & (1 << i)) n++;
  return n;
};

function luz(ctx: CanvasRenderingContext2D, x: number, y: number, raio: number, cor: string, forca: number) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, raio);
  g.addColorStop(0, cor);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, forca));
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, raio, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Uma ponta: `chama` (só a luz da cor, sem orbe), `orbe` (a orbe acesa no pedestal: engastada ou a ponta desperta) e
 * `forte` (a ponta do evento, pulsando). `sx, sy` = o pé do pedestal na tela.
 */
export function desenharPonta(ctx: CanvasRenderingContext2D, sx: number, sy: number, zoom: number, cor: CorPenta,
  modo: 'chama' | 'orbe' | 'forte', t: number) {
  const hex = COR_HEX[cor];
  const topo = sy - 60 * zoom;
  const pulso = 0.75 + 0.25 * Math.sin(t * 3);
  luz(ctx, sx, sy - 6 * zoom, 70 * zoom, hex, modo === 'forte' ? 0.55 * pulso : 0.22);
  if (modo === 'chama') {
    luz(ctx, sx, topo, 16 * zoom, hex, 0.6 * pulso);
    return;
  }
  luz(ctx, sx, topo, (modo === 'forte' ? 46 : 30) * zoom, hex, 0.8 * pulso);
  const r = 7 * zoom;
  ctx.save();
  const g = ctx.createRadialGradient(sx - r * 0.35, topo - r * 0.4, r * 0.1, sx, topo, r);
  g.addColorStop(0, '#ffffff');
  g.addColorStop(0.35, hex);
  g.addColorStop(1, 'rgba(0,0,0,0.85)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(sx, topo, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** O ritual: os raios das pontas até o centro e a luz no pentagrama. Pontos em coordenada de tela. */
export function desenharRitual(ctx: CanvasRenderingContext2D, pontas: readonly ({ x: number; y: number } | null)[],
  centro: { x: number; y: number }, zoom: number, mascara: number, ativo: boolean, t: number) {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  pontas.forEach((p, i) => {
    if (!p || !(mascara & (1 << i))) return;
    ctx.strokeStyle = COR_HEX[CORES_PENTA[i]];
    ctx.globalAlpha = ativo ? 0.55 + 0.25 * Math.sin(t * 6 + i) : 0.18;
    ctx.lineWidth = (ativo ? 5 : 2) * zoom;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(centro.x, centro.y);
    ctx.stroke();
  });
  ctx.restore();
  if (ativo) luz(ctx, centro.x, centro.y, 90 * zoom, '#c9a8ff', 0.75 + 0.2 * Math.sin(t * 4));
}

/* ---------------------------------------------------------------- o portal do santuário (techspec-pentagrama §02.1) */

/** O `efeito` dos portais de ida e volta do santuário (world_portals). O motor troca pelo sprite da cor. */
export const EFEITO_PORTAL_PENTA = 'portal-pentagrama';
/** Sem edição aberta, o portal passa pelas cinco cores, uma a cada tanto. */
const CICLO_S = 5;

export const spriteDoPortalPenta = (cor: number) => `portal-penta-${CORES_PENTA[cor] ?? 'vazio'}`;

/**
 * Que cor o portal mostra agora e com que força. Com a ponta desperta (`pentaCor >= 0`), a cor dela, cheia; sem
 * edição, o ciclo das cinco, com o fade da troca (0..1).
 */
export function corDoPortalPenta(pentaCor: number, t: number): { cor: number; alfa: number; desperto: boolean } {
  if (pentaCor >= 0 && pentaCor < 5) return { cor: pentaCor, alfa: 1, desperto: true };
  const fase = t / CICLO_S;
  const resto = fase - Math.floor(fase);
  return { cor: Math.floor(fase) % 5, alfa: Math.min(1, resto / 0.12, (1 - resto) / 0.12), desperto: false };
}

/** Pseudo-aleatório estável por partícula: o mesmo índice sempre cai no mesmo lugar (sem estado no motor). */
const sorte = (i: number, k: number) => {
  const s = Math.sin(i * 127.1 + k * 311.7) * 43758.5453;
  return s - Math.floor(s);
};

/**
 * O selo no chão, antes do sprite: um anel com a estrela de cinco pontas, achatado no losango, girando devagar.
 * `sx, sy` = o centro do tile na tela.
 */
export function desenharSeloPortal(ctx: CanvasRenderingContext2D, sx: number, sy: number, zoom: number, cor: number,
  alfa: number, desperto: boolean, t: number) {
  const hex = COR_HEX[CORES_PENTA[cor] ?? 'vazio'];
  const r = (desperto ? 30 : 24) * zoom;
  const pulso = 0.7 + 0.3 * Math.sin(t * (desperto ? 5 : 2));
  luz(ctx, sx, sy, r * 1.8, hex, (desperto ? 0.5 : 0.28) * alfa * pulso);
  ctx.save();
  ctx.translate(sx, sy);
  ctx.scale(1, 0.5);
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = (desperto ? 0.85 : 0.55) * alfa;
  ctx.strokeStyle = hex;
  ctx.shadowColor = hex;
  ctx.shadowBlur = 8 * zoom;
  ctx.lineWidth = 1.6 * zoom;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = 1.2 * zoom;
  const giro = t * (desperto ? 0.9 : 0.35);
  ctx.beginPath();
  for (let k = 0; k <= 5; k++) {
    const a = giro - Math.PI / 2 + (k * 2 * (Math.PI * 2)) / 5; // a estrela: de duas em duas pontas
    const x = Math.cos(a) * r * 0.92, y = Math.sin(a) * r * 0.92;
    if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.restore();
}

/**
 * As partículas da cor, depois do sprite (aditivo). Fogo: brasas subindo. Gelo: neve caindo e faíscas frias. Terra:
 * pedras orbitando a base. Ar: rajadas girando em espiral. Vazio: fiapos sugados para o centro e o miolo escuro.
 * Desperto, o dobro de partículas e um feixe para o alto.
 */
export function desenharParticulasPortal(ctx: CanvasRenderingContext2D, sx: number, sy: number, zoom: number,
  cor: number, alfa: number, desperto: boolean, t: number) {
  const id = CORES_PENTA[cor] ?? 'vazio';
  const hex = COR_HEX[id];
  const meio = sy - 35 * zoom; // o miolo do moongate (44×109, base 22 px abaixo do centro do tile)
  const n = desperto ? 26 : 13;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  if (desperto) {
    // o feixe: sai do topo do moongate, afinando para o alto, com as bordas apagadas
    const topo = sy - 80 * zoom, alto = 150 * zoom;
    const g = ctx.createLinearGradient(sx, topo, sx, topo - alto);
    g.addColorStop(0, hex);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    for (const [larg, forca] of [[12, 0.12], [7, 0.16], [3, 0.22]] as const) {
      ctx.globalAlpha = forca * (0.8 + 0.2 * Math.sin(t * 4)) * alfa;
      ctx.beginPath();
      ctx.moveTo(sx - larg * zoom, topo);
      ctx.lineTo(sx - larg * 0.4 * zoom, topo - alto);
      ctx.lineTo(sx + larg * 0.4 * zoom, topo - alto);
      ctx.lineTo(sx + larg * zoom, topo);
      ctx.closePath();
      ctx.fill();
    }
  }
  luz(ctx, sx, meio, (desperto ? 44 : 30) * zoom, hex, (desperto ? 0.55 : 0.3) * alfa);

  const ponto = (x: number, y: number, raio: number, c: string, a: number) => {
    ctx.globalAlpha = Math.max(0, Math.min(1, a)) * alfa;
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.6, raio * zoom), 0, Math.PI * 2);
    ctx.fill();
  };

  for (let i = 0; i < n; i++) {
    const v = 0.35 + sorte(i, 1) * 0.65;
    const f = (sorte(i, 2) + t * v * 0.45) % 1; // 0..1: a vida da partícula
    const lado = sorte(i, 3) * 2 - 1;
    switch (id) {
      case 'fogo': {
        const x = sx + lado * 16 * zoom + Math.sin(t * 6 + i) * 3 * zoom * f;
        const y = sy + 18 * zoom - f * 120 * zoom;
        ponto(x, y, 1.8 - f, f < 0.5 ? '#ffd27a' : '#ff6a2a', (1 - f) * 1.2);
        break;
      }
      case 'gelo': {
        const x = sx + lado * 26 * zoom + Math.sin(t * 1.5 + i * 2) * 5 * zoom;
        const y = meio - 70 * zoom + f * 100 * zoom;
        ponto(x, y, 1.4, '#e6f6ff', Math.sin(f * Math.PI));
        if (i % 4 === 0) {
          const b = 0.5 + 0.5 * Math.sin(t * 7 + i);
          ctx.globalAlpha = b * alfa * 0.9;
          ctx.strokeStyle = '#bfe6ff';
          ctx.lineWidth = 0.8 * zoom;
          const fx = sx + lado * 14 * zoom, fy = meio + (sorte(i, 4) - 0.5) * 60 * zoom, s = 3 * zoom * b;
          ctx.beginPath();
          ctx.moveTo(fx - s, fy); ctx.lineTo(fx + s, fy); ctx.moveTo(fx, fy - s); ctx.lineTo(fx, fy + s);
          ctx.stroke();
        }
        break;
      }
      case 'terra': {
        const a = t * v * 1.6 + sorte(i, 4) * Math.PI * 2;
        const x = sx + Math.cos(a) * 24 * zoom;
        const y = sy + 6 * zoom + Math.sin(a) * 10 * zoom - (8 + Math.sin(t * 2 + i) * 5) * zoom - f * 6 * zoom;
        const frente = Math.sin(a) > 0;
        ponto(x, y, frente ? 2.2 : 1.5, i % 3 ? '#b08a4a' : '#7ccf5a', frente ? 0.95 : 0.45);
        break;
      }
      case 'ar': {
        const a = t * (1.5 + v * 2) + sorte(i, 4) * Math.PI * 2;
        const raio = 18 + 8 * Math.sin(f * Math.PI);
        const y0 = sy + 10 * zoom - f * 100 * zoom;
        ctx.globalAlpha = Math.sin(f * Math.PI) * alfa * 0.8;
        ctx.strokeStyle = '#f2f6ff';
        ctx.lineWidth = 1.1 * zoom;
        ctx.beginPath();
        ctx.ellipse(sx, y0, raio * zoom, raio * 0.35 * zoom, 0, a, a + 0.9);
        ctx.stroke();
        break;
      }
      default: { // vazio
        const a = sorte(i, 4) * Math.PI * 2 + t * 0.8;
        const d = (1 - f) * 46;
        const x = sx + Math.cos(a + f * 3) * d * zoom;
        const y = meio + Math.sin(a + f * 3) * d * 0.8 * zoom;
        ponto(x, y, 1.6 * (1 - f) + 0.6, i % 2 ? '#c38cff' : '#6a2bbf', f * 1.1);
        break;
      }
    }
  }
  ctx.restore();
  if (id === 'vazio') {
    // o miolo escuro: o vazio come a luz em vez de somar
    ctx.save();
    const r = (desperto ? 12 : 9) * zoom * (0.85 + 0.15 * Math.sin(t * 3));
    const g = ctx.createRadialGradient(sx, meio, 0, sx, meio, r);
    g.addColorStop(0, 'rgba(8,0,16,0.9)');
    g.addColorStop(1, 'rgba(8,0,16,0)');
    ctx.globalAlpha = alfa;
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(sx, meio, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}
